import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { confirmDelete } from "@/lib/confirm";
import { toast } from "sonner";
import { Loader2, Trash2, ExternalLink } from "lucide-react";
import { LABEL, PositionBadge, ChangeChip } from "./kwShared";

const COMP_COLORS = ["#F59E0B", "#0EA5E9", "#EC4899"];
const RANGES = [30, 90, 180];

export function KeywordHistorySheet({ wid, kw, device, canEdit, onClose, onChanged }) {
  const [days, setDays] = useState(90);
  const [hist, setHist] = useState(null);
  const [tags, setTags] = useState((kw.tags || []).join(", "));
  const [target, setTarget] = useState(kw.target_url || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setHist(null);
    api.get(`/console/keywords/${wid}/keywords/${kw.id}/history`, { params: { device, days } }).then(({ data }) => setHist(data)).catch(() => setHist({ points: [], competitors: [] }));
  }, [wid, kw.id, device, days]);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/console/keywords/${wid}/keywords/${kw.id}`, { tags: tags.split(",").map((t) => t.trim()).filter(Boolean), target_url: target }); toast.success("Keyword updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async () => {
    if (!(await confirmDelete({ message: `Stop tracking "${kw.keyword}"? Its ranking history is removed.` }))) return;
    await api.post(`/console/keywords/${wid}/keywords/delete`, { ids: [kw.id] }); toast.success("Keyword removed"); onChanged(true);
  };
  const comps = hist?.competitors || [];
  const data = (hist?.points || []).map((p) => ({ date: p.date.slice(5), you: p.position ?? null, ...Object.fromEntries(comps.map((c) => [c, p.competitors?.[c] ?? null])) }));
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="bg-white w-full sm:max-w-xl overflow-y-auto" data-testid="kw-history-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-left">{kw.keyword}</SheetTitle>
          <SheetDescription className="text-left capitalize">{device} ranking history</SheetDescription>
        </SheetHeader>
        <div className="grid grid-cols-3 gap-2 mt-5">
          <div className="rounded-xl border border-[#EEEFEF] px-3 py-2.5"><p className={LABEL}>Now</p><div className="mt-1"><PositionBadge position={kw.position} testid="kw-history-now" /></div></div>
          <div className="rounded-xl border border-[#EEEFEF] px-3 py-2.5"><p className={LABEL}>7 days</p><div className="mt-1"><ChangeChip value={kw.change_7d} /></div></div>
          <div className="rounded-xl border border-[#EEEFEF] px-3 py-2.5"><p className={LABEL}>Best</p><div className="mt-1"><PositionBadge position={kw.best} /></div></div>
        </div>
        <div className="flex items-center justify-between mt-6 mb-2">
          <p className={LABEL}>Position over time</p>
          <div className="flex gap-1">{RANGES.map((r) => <button key={r} data-testid={`kw-range-${r}`} onClick={() => setDays(r)} className={`rounded-full px-2.5 py-0.5 text-[11px] ${days === r ? "bg-[#06060C] text-white" : "text-[#595D62] hover:bg-[#F6F6FD]"}`}>{r}d</button>)}</div>
        </div>
        <div className="h-60 rounded-2xl border border-[#EEEFEF] p-2" data-testid="kw-history-chart">
          {!hist ? <div className="h-full flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" /></div>
            : data.length === 0 ? <div className="h-full flex items-center justify-center text-sm text-[#9FA1A4]">No checks yet. History appears after the first daily run.</div> : (
            <ResponsiveContainer width="100%" height={222}>
              <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#f0f0f3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#9FA1A4" }} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis reversed domain={[1, "dataMax"]} allowDecimals={false} tick={{ fontSize: 10, fill: "#9FA1A4" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EEEFEF", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="you" name="Client" stroke="#723FFF" strokeWidth={2.5} dot={false} connectNulls />
                {comps.map((c, i) => <Line key={c} type="monotone" dataKey={c} stroke={COMP_COLORS[i]} strokeWidth={1.5} strokeDasharray="4 3" dot={false} connectNulls />)}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
        {kw.url && <a href={kw.url} target="_blank" rel="noreferrer" data-testid="kw-history-url" className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#723FFF] hover:underline break-all"><ExternalLink className="h-3 w-3 shrink-0" />{kw.url}</a>}
        {canEdit && (
          <div className="mt-6 space-y-3 border-t border-[#f0f0f3] pt-5">
            <div><label className={`${LABEL} mb-1 block`}>Tags</label><Input data-testid="kw-edit-tags" value={tags} onChange={(e) => setTags(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
            <div><label className={`${LABEL} mb-1 block`}>Target page</label><Input data-testid="kw-edit-target" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="https://..." className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
            <div className="flex items-center justify-between">
              <button data-testid="kw-delete" onClick={del} className="text-sm text-rose-600 hover:text-rose-700 flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Stop tracking</button>
              <Button data-testid="kw-edit-save" onClick={save} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
